import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Panel } from "../../components/ui/dashboard";

type Mode = "add" | "list" | "edit";

interface Props {
  mode: Mode;
}

// --- Transporter Form ---
const emptyTransporterForm = {
  company_name: "",
  user_id: "",
  fleet_size: "",
  license_number: "",
};

// --- Driver Form ---
const emptyDriverForm = {
  user_id: "",
  license_number: "",
  license_expiry: "",
};

// --- Vehicle Form ---
const emptyVehicleForm = {
  registration_number: "",
  vehicle_type: "",
  capacity_kg: "",
  is_available: true,
};

const AdminTransporterPage: React.FC<Props> = ({ mode }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [transporterForm, setTransporterForm] = useState(emptyTransporterForm);
  const [driverForm, setDriverForm] = useState(emptyDriverForm);
  const [vehicleForm, setVehicleForm] = useState(emptyVehicleForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Details for editing - including loaded drivers & vehicles
  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);

  useEffect(() => {
    if (!apiUrl) return;
    if (mode === "list") {
      fetch(`${apiUrl}/auth/transporters`)
        .then((r) => r.json())
        .then((d) => setRows(Array.isArray(d) ? d : []))
        .catch(() => setRows([]));
    }
  }, [apiUrl, mode]);

  // Load transporter details (edit mode), plus drivers and vehicles
  useEffect(() => {
    if (!apiUrl || mode !== "edit" || !id) return;
    setLoading(true);
    Promise.all([
      fetch(`${apiUrl}/auth/transporters/${id}`).then((r) => r.json()),
      fetch(`${apiUrl}/auth/drivers?transporter_id=${id}`).then((r) => r.json()),
      fetch(`${apiUrl}/auth/vehicles?transporter_id=${id}`).then((r) => r.json())
    ])
      .then(([transporterData, driversData, vehiclesData]) => {
        setTransporterForm({
          company_name: transporterData?.company_name || "",
          user_id: transporterData?.user_id ? String(transporterData.user_id) : "",
          fleet_size: transporterData?.fleet_size ? String(transporterData.fleet_size) : "",
          license_number: transporterData?.license_number || "",
        });
        setDrivers(Array.isArray(driversData) ? driversData : []);
        setVehicles(Array.isArray(vehiclesData) ? vehiclesData : []);
      })
      .catch(() => setMessage("Could not load transporter details"))
      .finally(() => setLoading(false));
  }, [apiUrl, mode, id]);

  const saveTransporter = async () => {
    if (!apiUrl) return;
    setLoading(true);
    setMessage(null);
    try {
      const payload = {
        ...transporterForm,
        user_id: Number(transporterForm.user_id),
        fleet_size: Number(transporterForm.fleet_size || 0),
      };
      const endpoint = mode === "edit" && id ? `${apiUrl}/auth/transporters/${id}` : `${apiUrl}/auth/transporters`;
      const method = mode === "edit" ? "PUT" : "POST";
      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to save transporter");
      setMessage(mode === "edit" ? "Transporter updated" : "Transporter created");
      if (mode === "add") setTransporterForm(emptyTransporterForm);
      if (mode === "edit") navigate("/admin/transporters/list");
    } catch (e: any) {
      setMessage(e?.message || "Failed to save transporter");
    } finally {
      setLoading(false);
    }
  };

  // Add Driver through API (POST /auth/drivers)
  const saveDriver = async () => {
    if (!apiUrl || !id) return;
    setLoading(true);
    setMessage(null);
    try {
      const payload = {
        ...driverForm,
        transporter_id: Number(id),
        user_id: Number(driverForm.user_id),
      };
      const res = await fetch(`${apiUrl}/auth/drivers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to add driver");
      setMessage("Driver added");
      setDriverForm(emptyDriverForm);
      // Refresh driver list
      const dRes = await fetch(`${apiUrl}/auth/drivers?transporter_id=${id}`);
      setDrivers(await dRes.json());
    } catch (e: any) {
      setMessage(e?.message || "Failed to add driver");
    } finally {
      setLoading(false);
    }
  };

  // Add Vehicle through API (POST /auth/vehicles)
  const saveVehicle = async () => {
    if (!apiUrl || !id) return;
    setLoading(true);
    setMessage(null);
    try {
      const payload = {
        ...vehicleForm,
        transporter_id: Number(id),
        capacity_kg: Number(vehicleForm.capacity_kg || 0),
        is_available: vehicleForm.is_available ? 1 : 0,
      };
      const res = await fetch(`${apiUrl}/auth/vehicles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to add vehicle");
      setMessage("Vehicle added");
      setVehicleForm(emptyVehicleForm);
      // Refresh vehicle list
      const vRes = await fetch(`${apiUrl}/auth/vehicles?transporter_id=${id}`);
      setVehicles(await vRes.json());
    } catch (e: any) {
      setMessage(e?.message || "Failed to add vehicle");
    } finally {
      setLoading(false);
    }
  };

  if (mode === "list") {
    return (
      <div className="p-4 md:p-6">
        <Panel title="Transporters - List" subtitle="Show all transporters with edit option">
          <div className="mb-3">
            <Link to="/admin/transporters/add" className="px-3 py-2 rounded-lg text-sm bg-slate-900 text-white">Add Transporter</Link>
          </div>
          <div className="space-y-2">
            {rows.map((row) => (
              <div key={row.id} className="rounded-xl border border-slate-100 p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{row.company_name}</p>
                  <p className="text-xs text-slate-500">Fleet: {row.fleet_size} · License: {row.license_number}</p>
                </div>
                <Link to={`/admin/transporters/edit/${row.id}`} className="px-2 py-1 text-xs rounded-md bg-slate-100 text-slate-700">Edit</Link>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <Panel title={`${mode === "edit" ? "Edit" : "Add"} Transporter`} subtitle="Manage transporter details including drivers and vehicles">
        {/* Transporter Details Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            className="border rounded-lg px-3 py-2 text-sm"
            placeholder="Company Name"
            value={transporterForm.company_name}
            onChange={(e) => setTransporterForm((p) => ({ ...p, company_name: e.target.value }))}
          />
          <input
            className="border rounded-lg px-3 py-2 text-sm"
            placeholder="Linked User ID"
            value={transporterForm.user_id}
            onChange={(e) => setTransporterForm((p) => ({ ...p, user_id: e.target.value }))}
          />
          <input
            className="border rounded-lg px-3 py-2 text-sm"
            placeholder="Fleet Size"
            value={transporterForm.fleet_size}
            onChange={(e) => setTransporterForm((p) => ({ ...p, fleet_size: e.target.value }))}
          />
          <input
            className="border rounded-lg px-3 py-2 text-sm"
            placeholder="License Number"
            value={transporterForm.license_number}
            onChange={(e) => setTransporterForm((p) => ({ ...p, license_number: e.target.value }))}
          />
        </div>
        {/* Add driver & vehicle forms only in edit mode */}
        {mode === "edit" && (
          <>
            <Panel title="Add Driver" >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder="Driver User ID"
                  value={driverForm.user_id}
                  onChange={(e) => setDriverForm((p) => ({ ...p, user_id: e.target.value }))}
                />
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder="License Number"
                  value={driverForm.license_number}
                  onChange={(e) => setDriverForm((p) => ({ ...p, license_number: e.target.value }))}
                />
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder="License Expiry"
                  type="date"
                  value={driverForm.license_expiry}
                  onChange={(e) => setDriverForm((p) => ({ ...p, license_expiry: e.target.value }))}
                />
                <button onClick={saveDriver} disabled={loading} className="px-3 py-2 rounded-lg text-sm bg-indigo-600 text-white disabled:opacity-60 mt-2 md:mt-0">
                  Add Driver
                </button>
              </div>
              <div className="mt-4">
                <strong>Drivers:</strong>
                <div className="space-y-1 mt-2">
                  {(drivers || []).length === 0 && <p className="text-xs text-slate-500">No drivers</p>}
                  {drivers.map((driver: any) => (
                    <div key={driver.id || driver.user_id || Math.random()} className="rounded border p-2 flex flex-col md:flex-row justify-between md:items-center">
                      <span>
                        <b>ID:</b> {driver.user_id} &nbsp;<b>Lic:</b> {driver.license_number} &nbsp;
                        <b>Expiry:</b> {driver.license_expiry}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>

            <Panel title="Add Vehicle" >
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder="Registration Number"
                  value={vehicleForm.registration_number}
                  onChange={(e) => setVehicleForm((p) => ({ ...p, registration_number: e.target.value }))}
                />
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder="Vehicle Type"
                  value={vehicleForm.vehicle_type}
                  onChange={(e) => setVehicleForm((p) => ({ ...p, vehicle_type: e.target.value }))}
                />
                <input
                  className="border rounded-lg px-3 py-2 text-sm"
                  placeholder="Capacity (kg)"
                  type="number"
                  value={vehicleForm.capacity_kg}
                  onChange={(e) => setVehicleForm((p) => ({ ...p, capacity_kg: e.target.value }))}
                />
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={vehicleForm.is_available}
                    onChange={(e) => setVehicleForm((p) => ({ ...p, is_available: e.target.checked }))}
                  />
                  Available
                </label>
                <button onClick={saveVehicle} disabled={loading} className="px-3 py-2 rounded-lg text-sm bg-indigo-600 text-white disabled:opacity-60 mt-2 md:mt-0">
                  Add Vehicle
                </button>
              </div>
              <div className="mt-4">
                <strong>Vehicles:</strong>
                <div className="space-y-1 mt-2">
                  {(vehicles || []).length === 0 && <p className="text-xs text-slate-500">No vehicles</p>}
                  {vehicles.map((vehicle: any) => (
                    <div key={vehicle.id || vehicle.registration_number || Math.random()} className="rounded border p-2 flex flex-col md:flex-row justify-between md:items-center">
                      <span>
                        <b>Reg:</b> {vehicle.registration_number} &nbsp;
                        <b>Type:</b> {vehicle.vehicle_type} &nbsp;
                        <b>Capacity:</b> {vehicle.capacity_kg}kg &nbsp;
                        <b>Available:</b> {vehicle.is_available ? "Yes" : "No"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          </>
        )}

        {message ? <p className="text-xs text-slate-600 mt-3">{message}</p> : null}
        <div className="mt-4 flex justify-end gap-2">
          <Link to="/admin/transporters/list" className="px-3 py-2 rounded-lg text-sm bg-slate-100 text-slate-700">Back</Link>
          <button onClick={saveTransporter} disabled={loading} className="px-3 py-2 rounded-lg text-sm bg-indigo-600 text-white disabled:opacity-60">{loading ? "Saving..." : mode === "edit" ? "Update" : "Create"}</button>
        </div>
      </Panel>
    </div>
  );
};

export default AdminTransporterPage;
